'use client'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api-fetch'
import { queryKeys } from '../keys'
import type { ImageTextProjectDTO, ImageTextSettingsDTO } from '@/types/image-text'

export type ImageTextProjectsResponse = {
  projects: ImageTextProjectDTO[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

export function useImageTextProjects(params: { page: number; pageSize: number; search: string; enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.imageText.projects(params.page, params.pageSize, params.search.trim()),
    enabled: params.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async (): Promise<ImageTextProjectsResponse> => {
      const search = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) })
      if (params.search.trim()) search.set('search', params.search.trim())
      const res = await apiFetch(`/api/image-text/projects?${search}`)
      if (!res.ok) throw new Error('Failed to fetch image text projects')
      return res.json()
    },
  })
}

export function useImageTextProject(projectId?: string | null) {
  return useQuery({
    queryKey: queryKeys.imageText.project(projectId || ''),
    enabled: !!projectId,
    staleTime: 5_000,
    queryFn: async (): Promise<{ project: ImageTextProjectDTO }> => {
      const res = await apiFetch(`/api/image-text/projects/${projectId}`)
      if (!res.ok) throw new Error('Failed to fetch image text project')
      return res.json()
    },
  })
}

export function useImageTextSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.imageText.settings(),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    queryFn: async (): Promise<{ settings: ImageTextSettingsDTO }> => {
      const res = await apiFetch('/api/image-text/settings')
      if (!res.ok) throw new Error('Failed to fetch image text settings')
      return res.json()
    },
  })
}
